import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  GalleryContainer,
  MainImage,
  MainImageWrapper,
  Thumbnail,
  ThumbnailsRow,
  ThumbnailsWrapper,
  HideOnMobile,
  MobileStepperWrapper
} from './styles';
import { IconChevronRight, IconChevronLeft, Stepper } from '@veneer/core';
import useIsMobile from 'src/hooks/useIsMobile';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { ProductImage } from 'src/types/graphql/product';

interface PrinterDetailsProps {
  nonCurrentPrinterDetails?: PrinterItem[];
}
const ImageGallery = ({
  nonCurrentPrinterDetails
}: PrinterDetailsProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile();
  const Images: ProductImage[] = useMemo(() => {
    return (
      nonCurrentPrinterDetails?.find((p) => p.recommendedPrinter === true)
        ?.mediaGallery ?? []
    );
  }, [nonCurrentPrinterDetails]);

  const changeImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const stepperSteps = useMemo(() => {
    return Images.map((_, index) => {
      if (index < currentIndex) {
        return {
          label: `Image ${index + 1}`,
          status: 'incomplete' as const,
          step: undefined
        };
      } else if (index === currentIndex) {
        return {
          label: `Image ${index + 1}`,
          status: 'complete' as const,
          step: undefined
        };
      } else {
        return {
          label: `Image ${index + 1}`,
          status: 'incomplete' as const,
          step: undefined
        };
      }
    });
  }, [currentIndex]);

  const handleStepperChange = useCallback(
    (event: React.SyntheticEvent | undefined, index: number = 0) => {
      setCurrentIndex(index);
    },
    []
  );

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + Images.length) % Images.length);
  }, []);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % Images.length);
  }, []);

  return (
    <GalleryContainer>
      <MainImageWrapper>
        <MainImage src={Images[currentIndex]?.url} />
      </MainImageWrapper>

      <ThumbnailsRow>
        {/* Prev Button (desktop only) */}
        <HideOnMobile>
          <Button
            onClick={prevImage}
            aria-label="Previous image"
          >
            <IconChevronLeft />
          </Button>
        </HideOnMobile>

        <ThumbnailsWrapper>
          {isMobile
            ? null
            : Images.map((img, index) => {
                const key = img.url ?? `image-${index}`;
                const goToImage = () => changeImage(index);
                const onKey = (e: React.KeyboardEvent) =>
                  e.key === 'Enter' && changeImage(index);
                return (
                  <Thumbnail
                    key={key}
                    src={img.url}
                    active={currentIndex === index}
                    onClick={goToImage}
                    role="button"
                    tabIndex={0}
                    aria-label={`Go to image ${index + 1}`}
                    onKeyDown={onKey}
                  />
                );
              })}
        </ThumbnailsWrapper>

        {/* Next Button (desktop only) */}
        <HideOnMobile>
          <Button
            onClick={nextImage}
            aria-label="Next image"
          >
            <IconChevronRight />
          </Button>
        </HideOnMobile>
      </ThumbnailsRow>

      {isMobile && (
        <MobileStepperWrapper>
          <Stepper
            appearance="indicator"
            size={24}
            steps={stepperSteps}
            interactive={true}
            onChange={handleStepperChange}
          />
        </MobileStepperWrapper>
      )}
    </GalleryContainer>
  );
};

export default ImageGallery;
